import { createSignal, Show, For, onMount, onCleanup, createEffect, createMemo } from 'solid-js'
import { userStore, settingStore } from '/web/store'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { toastStore } from '/web/store'
import { tokenOptions } from '/common/token-plans'
import { useGoogleReady } from '/web/shared/hooks'
import './BuyTokens.css'

// ─── Package data (buy modal – paid packages) ─────────────────────────────────

const BUY_PACKAGES = tokenOptions // [50, 100, 200, 400] when hasClaimedInitial

interface Badge {
  label: string
  gradient: string
  textColor: string
}

const BADGES: Record<number, Badge | undefined> = {
  100: {
    label: '⚡ Most Popular',
    gradient: 'linear-gradient(135deg, #8A2BE2, #00E5FF)',
    textColor: '#fff',
  },
  400: {
    label: '🏆 Best Value',
    gradient: 'linear-gradient(90deg, #FF8A3C, #FFD93D)',
    textColor: '#1a0e2e',
  },
}

const BUY_PERKS = ['Live 24/7 Chat', 'AI Image Generation', '1,000+ Characters']
const FREE_PERKS = ['Live 24/7 Chat', 'AI Image Generation', '1,000+ Characters']

// ─── Shared close button ──────────────────────────────────────────────────────

const CloseBtn = (props: {
  onClose: () => void
  top?: number
  right?: number
  size?: number
  extraClass?: string
}) => {
  const size = props.size ?? 34
  const top = props.top ?? 14
  const right = props.right ?? 14
  return (
    <button
      onClick={props.onClose}
      class={`modal-close-btn${props.extraClass ? ' ' + props.extraClass : ''}`}
      type="button"
      style={`position:absolute;top:${top}px;right:${right}px;width:${size}px;height:${size}px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.1);color:rgba(255,255,255,0.7);font-size:22px;cursor:pointer;z-index:10`}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(255,60,172,0.18)'
        e.currentTarget.style.color = 'white'
        e.currentTarget.style.transform = 'rotate(90deg)'
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(255,255,255,0.06)'
        e.currentTarget.style.color = 'rgba(255,255,255,0.7)'
        e.currentTarget.style.transform = 'rotate(0deg)'
      }}
    >
      &#10005;
    </button>
  )
}

// ─── Payment row (shared) ─────────────────────────────────────────────────────

const PaymentRow = (props: { radioClass: string; class?: string }) => (
  <div
    class={props.class}
    style="background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08);border-radius:10px;padding:13px 18px;margin-bottom:18px;display:flex;align-items:center;gap:12px"
  >
    <div class={props.radioClass} />
    <div
      class="bt-payment-label"
      style="font-family:'Rajdhani',sans-serif;font-size:14px;font-weight:700;color:rgba(255,255,255,0.75);letter-spacing:0.04em"
    >
      Credit / Debit Card
    </div>
    <div class="bt-card-logos" style="margin-left:auto;display:flex;align-items:center;gap:6px">
      <img
        src="/images/footer/visa-card.svg"
        alt="Visa"
        style="height:22px;width:auto;border-radius:4px"
      />
      <img
        src="/images/footer/mastercard-card.svg"
        alt="Mastercard"
        style="height:22px;width:auto;border-radius:4px"
      />
      <img
        src="/images/footer/discover-card.svg"
        alt="Discover"
        style="height:22px;width:auto;border-radius:4px"
      />
    </div>
  </div>
)

// ─── BUY TOKENS MODAL (paid purchase flow) ───────────────────────────────────

const BuyTokensFlow = (props: {
  onClose: () => void
  onPurchase: (id: number, price: string, amount: number) => Promise<void>
  // #212: 'empty' swaps the banner + section label for the "You're Running On Empty"
  // out-of-tokens context; the packages / payment / CTA below are identical.
  variant?: 'default' | 'empty'
}) => {
  const [selected, setSelected] = createSignal(50)

  const selectedPkg = createMemo(() => BUY_PACKAGES.find((p) => p.amount === selected()))

  return (
    <>
      {/* Banner */}
      {/* #212: out-of-tokens variant swaps this banner for the "Running On Empty" one. */}
      <Show when={props.variant === 'empty'}>
        <div
          class="bt-modal-banner bt-banner-empty"
          style="padding:30px 24px 22px;text-align:center;background:linear-gradient(135deg,#1a0a2e 0%,#0d0d1a 50%,#1a1010 100%);border-bottom:1px solid rgba(255,198,43,0.22)"
        >
          <CloseBtn onClose={props.onClose} extraClass="bt-close" />

          {/* Gold coin badge */}
          <div style="position:relative;z-index:1;width:72px;height:72px;margin:0 auto 14px;border-radius:50%;display:grid;place-items:center;background:radial-gradient(circle at 50% 38%,rgba(255,198,43,0.22),rgba(138,43,226,0.12) 70%);box-shadow:0 0 0 5px rgba(255,198,43,0.10),0 0 0 11px rgba(138,43,226,0.05),0 10px 24px rgba(255,198,43,0.26)">
            <svg viewBox="0 0 48 48" fill="none" aria-hidden="true" style="width:40px;height:40px;display:block">
              <circle cx="24" cy="24" r="20" fill="#FFC62B" stroke="#E0A400" stroke-width="2" />
              <circle cx="24" cy="24" r="15" fill="none" stroke="#B98600" stroke-width="1.5" opacity="0.55" />
              <text
                x="24"
                y="33"
                font-family="'Bebas Neue',sans-serif"
                font-size="26"
                font-weight="800"
                fill="#7a5200"
                text-anchor="middle"
              >
                $
              </text>
            </svg>
          </div>

          {/* Eyebrow — gold */}
          <div style="font-family:'Rajdhani',sans-serif;font-weight:700;font-size:12px;letter-spacing:2.4px;text-transform:uppercase;color:#FFC62B;margin-bottom:8px">
            Out of Tokens
          </div>

          {/* Headline — "You're Running On Empty" (On=cyan, Empty=pink) */}
          <h2 style="font-family:'Bebas Neue',sans-serif;font-weight:400;font-size:38px;line-height:1;letter-spacing:1px;text-transform:uppercase;margin:0;color:#00E5FF;text-shadow:0 0 16px rgba(0,229,255,0.55),0 2px 18px rgba(0,0,0,0.7)">
            You're Running On{' '}
            <em style="font-style:normal;color:#FF3CAC;text-shadow:0 0 16px rgba(255,60,172,0.6),0 2px 18px rgba(0,0,0,0.7)">
              Empty
            </em>
          </h2>
        </div>
      </Show>

      <Show when={props.variant !== 'empty'}>
      <div
        class="bt-modal-banner"
        style="padding:26px 28px 22px;background:linear-gradient(135deg,#1a0a2e 0%,#0d0d1a 50%,#1a1010 100%);border-bottom:1px solid rgba(138,43,226,0.22)"
      >
        <CloseBtn onClose={props.onClose} extraClass="bt-close" />

        {/* Eyebrow */}
        <div class="bt-banner-eyebrow">Power Your Experience</div>

        {/* Headline – Unlock(cyan) Premium(pink) / With(pink) Tokens(cyan) */}
        <div
          class="bt-headline"
          style="font-family:'Bebas Neue',sans-serif;font-weight:400;font-size:40px;line-height:0.98;text-transform:uppercase;letter-spacing:1px;position:relative;z-index:1;margin-bottom:12px"
        >
          <span style="color:#00E5FF;text-shadow:0 0 16px rgba(0,229,255,0.55),0 2px 18px rgba(0,0,0,0.7)">
            Unlock
          </span>{' '}
          <span style="color:#FF3CAC;text-shadow:0 0 16px rgba(255,60,172,0.6),0 2px 18px rgba(0,0,0,0.7)">
            Premium
          </span>
          <br />
          <span style="color:#FF3CAC;text-shadow:0 0 16px rgba(255,60,172,0.55),0 2px 18px rgba(0,0,0,0.7)">
            With
          </span>{' '}
          <span style="color:#00E5FF;text-shadow:0 0 16px rgba(0,229,255,0.55),0 2px 18px rgba(0,0,0,0.7)">
            Tokens
          </span>
        </div>

        {/* Sub-paragraph */}
        <div
          class="bt-subtitle"
          style="font-family:'Nunito',sans-serif;font-size:14px;line-height:1.55;color:rgba(255,255,255,0.58);max-width:420px;position:relative;z-index:1"
        >
          Tokens fuel everything — live chats, exclusive content, and AI image generation. Choose a
          package and start exploring.
        </div>

        {/* Perks */}
        <div
          class="bt-perks"
          style="display:flex;flex-wrap:wrap;gap:12px 18px;margin-top:18px;position:relative;z-index:1"
        >
          <For each={BUY_PERKS}>
            {(perk) => (
              <div style="display:flex;align-items:center;gap:6px;white-space:nowrap;flex-shrink:0;font-family:'Rajdhani',sans-serif;font-size:12px;font-weight:700;color:rgba(255,255,255,0.62);letter-spacing:0.04em">
                <span style="width:6px;height:6px;border-radius:50%;background:#00E5FF;box-shadow:0 0 6px #00E5FF;flex-shrink:0" />
                {perk}
              </div>
            )}
          </For>
        </div>
      </div>
      </Show>

      {/* Body */}
      <div class="bt-body" style="padding:20px 28px 24px">
        {/* Section label */}
        <div
          class="bt-section-label"
          style="font-family:'Rajdhani',sans-serif;font-size:11px;font-weight:700;letter-spacing:1.8px;text-transform:uppercase;color:rgba(255,255,255,0.38);margin-bottom:14px"
        >
          {props.variant === 'empty' ? 'Choose a Package to Top Up' : 'Select Your Token Package'}
        </div>

        {/* Package cards */}
        <div
          class="bt-pkg-list"
          style="display:flex;flex-direction:column;gap:8px;margin-bottom:18px"
        >
          <For each={BUY_PACKAGES}>
            {(pkg) => {
              const isSelected = () => selected() === pkg.amount
              const badge = BADGES[pkg.amount]
              return (
                <>
                  {/* Card */}
                  <div
                    class={`bt-pkg-card${badge ? ' bt-pkg-card--badge' : ''}`}
                    style={{
                      background: isSelected()
                        ? 'linear-gradient(135deg,rgba(0,229,255,0.10),rgba(138,43,226,0.10))'
                        : 'rgba(255,255,255,0.03)',
                      border: isSelected()
                        ? '1.5px solid rgba(0,229,255,0.55)'
                        : '1.5px solid rgba(255,255,255,0.08)',
                      'box-shadow': isSelected() ? '0 8px 24px rgba(0,229,255,0.12)' : 'none',
                      'border-radius': '10px',
                      padding: '14px 18px',
                      'margin-top': '0',
                      display: 'flex',
                      'flex-direction': 'column',
                      'align-items': 'stretch',
                      gap: badge ? '7px' : '0',
                      cursor: 'pointer',
                      position: 'relative',
                      transition:
                        'background 0.15s,border-color 0.15s,box-shadow 0.15s,transform 0.15s',
                    }}
                    onClick={() => setSelected(pkg.amount)}
                    onMouseEnter={(e) => {
                      if (!isSelected()) {
                        e.currentTarget.style.borderColor = 'rgba(0,229,255,0.36)'
                        e.currentTarget.style.background = 'rgba(0,229,255,0.04)'
                        e.currentTarget.style.transform = 'translateY(-1px)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected()) {
                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'
                        e.currentTarget.style.background = 'rgba(255,255,255,0.03)'
                        e.currentTarget.style.transform = 'translateY(0)'
                      }
                    }}
                  >
                    {/* Badge – inside the row, on its own line at the top-left */}
                    <Show when={badge}>
                      <span
                        class="bt-badge"
                        style={`align-self:flex-start;display:inline-block;font-family:'Rajdhani',sans-serif;font-size:10px;font-weight:800;letter-spacing:1.2px;text-transform:uppercase;padding:3px 10px;border-radius:999px;background:${
                          badge!.gradient
                        };color:${badge!.textColor};box-shadow:0 2px 8px rgba(0,0,0,0.35)`}
                      >
                        {badge!.label}
                      </span>
                    </Show>

                    {/* Content row: radio + amount + price */}
                    <div style="display:flex;align-items:center;gap:12px;width:100%">
                      {/* Radio */}
                      <div style="position:relative;flex-shrink:0">
                        <Show when={isSelected()}>
                          <div class="bt-radio-pulse" />
                        </Show>
                        <div
                          class={`bt-radio-dot${isSelected() ? ' active' : ''}`}
                          style={{
                            width: '22px',
                            height: '22px',
                            border: `2px solid ${
                              isSelected() ? '#00E5FF' : 'rgba(255,255,255,0.28)'
                            }`,
                          }}
                        />
                      </div>

                      {/* Amount + label */}
                      <div style="flex:1;display:flex;align-items:baseline;gap:6px">
                        <span
                          class="bt-pkg-amount"
                          style="font-family:'Bebas Neue',sans-serif;font-weight:400;font-size:36px;line-height:1;color:#fff;letter-spacing:1px"
                        >
                          {pkg.amount}
                        </span>
                        <span
                          class="bt-pkg-word"
                          style={`font-family:'Rajdhani',sans-serif;font-size:12px;font-weight:700;letter-spacing:1.4px;text-transform:uppercase;color:${
                            isSelected() ? '#00E5FF' : 'rgba(255,255,255,0.4)'
                          };transition:color 0.15s`}
                        >
                          Tokens
                        </span>
                      </div>

                      {/* Price */}
                      <div
                        class="bt-pkg-price"
                        style="font-family:'Bebas Neue',sans-serif;font-weight:400;font-size:28px;line-height:1;color:#fff;letter-spacing:0.5px;text-align:right"
                      >
                        ${pkg.price}
                      </div>
                    </div>
                  </div>
                </>
              )
            }}
          </For>
        </div>

        {/* Hairline divider */}
        <div
          class="bt-divider"
          style="height:1px;background:rgba(255,255,255,0.06);margin:18px 0"
        />

        {/* Payment row – cyan radio */}
        <PaymentRow radioClass="bt-payment-radio" class="bt-payment-row" />

        {/* CTA – multi gradient (cyan → purple → pink) */}
        <button
          class="bt-cta-btn"
          style="padding:17px;background:linear-gradient(135deg,#8A2BE2,#00E5FF);border-radius:10px;font-family:'Rajdhani',sans-serif;font-size:14px;font-weight:800;letter-spacing:1.4px;text-transform:uppercase;color:#fff;box-shadow:0 12px 36px rgba(138,43,226,0.4)"
          onClick={() => {
            const pkg = selectedPkg()
            if (!pkg) return
            props.onPurchase(pkg.id, pkg.price, pkg.amount)
          }}
        >
          <span style="white-space:nowrap">
            Continue to Checkout&nbsp;&mdash;&nbsp;${selectedPkg()?.price ?? ''}
          </span>
          <span class="bt-cta-arrow" style="font-size:18px">
            &rarr;
          </span>
        </button>

        {/* Fine print */}
        <div
          class="bt-fineprint"
          style="display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:14px;margin-top:12px;font-family:'Rajdhani',sans-serif;font-size:12px;font-weight:600;letter-spacing:0.6px;color:rgba(255,255,255,0.4);text-align:center"
        >
          <span style="display:flex;align-items:center;gap:5px">&#x1F512; Secure Checkout</span>
          <span style="display:flex;align-items:center;gap:5px">&#x2726; Instant Delivery</span>
          <span style="display:flex;align-items:center;gap:5px">&#x2726; Cancel Anytime</span>
        </div>
      </div>
    </>
  )
}

// ─── FREE TOKENS MODAL – Token card (hero element) ────────────────────────────

const FreeTokenCard = () => (
  <div
    class="tm-token-card"
    style="display:flex;align-items:center;gap:20px;padding:24px;border-radius:12px;background:linear-gradient(135deg,rgba(138,43,226,0.12),rgba(138,43,226,0.04));border:1.5px solid rgba(138,43,226,0.45);position:relative;padding-top:36px"
  >
    {/* Free badge */}
    <div style="position:absolute;top:0;right:0;background:linear-gradient(90deg,#FF3CAC,#FF6BC5);color:#fff;font-family:'Rajdhani',sans-serif;font-size:11px;font-weight:800;letter-spacing:0.14em;text-transform:uppercase;padding:5px 14px;border-radius:0 12px 0 10px">
      🎁 Free
    </div>

    {/* Left: radio + amount */}
    <div style="display:flex;align-items:center;gap:16px">
      {/* Radio */}
      <div style="position:relative;flex-shrink:0">
        <div class="tm-radio-pulse" />
        <div class="tm-radio-dot" />
      </div>

      {/* Amount */}
      <div>
        <div style="font-family:'Bebas Neue',sans-serif;font-size:64px;line-height:1;letter-spacing:-1px;color:#fff;font-weight:900">
          25
        </div>
        <div style="font-family:'Rajdhani',sans-serif;font-size:13px;font-weight:700;letter-spacing:0.2em;text-transform:uppercase;color:#8A2BE2">
          Tokens
        </div>
      </div>
    </div>

    {/* Right: pricing */}
    <div style="margin-left:auto;text-align:right">
      {/* Was price */}
      <div style="font-family:'Nunito',sans-serif;font-size:15px;font-weight:700;color:rgba(255,255,255,0.85);text-decoration:line-through;text-decoration-color:rgba(255,90,90,0.9);text-decoration-thickness:2px;margin-bottom:4px">
        $5.99
      </div>
      {/* Now price */}
      <div style="font-family:'Bebas Neue',sans-serif;font-size:42px;font-weight:900;line-height:1;color:#00E5FF;letter-spacing:-0.01em;text-shadow:0 0 16px rgba(0,229,255,0.55)">
        FREE
      </div>
      {/* Today Only label */}
      <div style="font-family:'Rajdhani',sans-serif;font-size:11px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:rgba(0,229,255,0.8);margin-top:4px">
        Today Only
      </div>
    </div>
  </div>
)

// ─── FREE TOKENS MODAL – Logged-in single column ──────────────────────────────

const FreeTokensSingleColumn = (props: { onClose: () => void; onClaim: () => Promise<void> }) => (
  <>
    {/* Banner */}
    <div
      class="tm-banner"
      style="padding:32px 36px 28px;background:linear-gradient(135deg,#1a1040 0%,#0B0B0F 50%,#13102a 100%);border-bottom:1px solid rgba(138,43,226,0.18)"
    >
      <CloseBtn onClose={props.onClose} top={16} right={16} size={32} />

      {/* Eyebrow */}
      <div class="tm-eyebrow">Limited Time Offer</div>

      {/* Headline */}
      <div style="font-family:'Bebas Neue',sans-serif;font-size:52px;font-weight:900;line-height:0.98;text-transform:uppercase;position:relative;z-index:1;margin-bottom:12px">
        <span style="color:#fff">Get </span>
        <span style="color:#00E5FF;text-shadow:0 0 16px rgba(0,229,255,0.55)">25 Tokens</span>
        <br />
        <span style="color:rgba(255,255,255,0.92)">Completely </span>
        <span style="color:#fff">Free</span>
      </div>

      {/* Sub */}
      <div style="font-family:'Nunito',sans-serif;font-size:14px;line-height:1.55;color:rgba(255,255,255,0.6);max-width:380px;position:relative;z-index:1">
        Unlock live chats and AI image generation — no charge, ever.
      </div>

      {/* Perks – 3 items, purple dots */}
      <div style="display:flex;flex-wrap:wrap;gap:12px 18px;margin-top:18px;position:relative;z-index:1">
        <For each={FREE_PERKS}>
          {(perk) => (
            <div style="display:flex;align-items:center;gap:6px;font-family:'Rajdhani',sans-serif;font-size:12.5px;font-weight:600;color:rgba(255,255,255,0.65)">
              <span style="width:6px;height:6px;border-radius:50%;background:#8A2BE2;flex-shrink:0" />
              {perk}
            </div>
          )}
        </For>
      </div>
    </div>

    {/* Body */}
    <div style="padding:24px 28px">
      {/* Token hero card */}
      <div style="margin-bottom:20px">
        <FreeTokenCard />
      </div>

      {/* Payment row – purple radio */}
      <PaymentRow radioClass="tm-payment-radio" />

      {/* CTA – hot gradient (purple → pink) */}
      <button
        class="tm-cta-btn"
        style="padding:18px;background:linear-gradient(135deg,#8A2BE2,#FF3CAC);border-radius:10px;font-family:'Rajdhani',sans-serif;font-size:17px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#fff;box-shadow:0 8px 30px rgba(138,43,226,0.35)"
        onClick={props.onClaim}
      >
        <span>Claim My Free Tokens</span>
        <span class="tm-cta-arrow" style="font-size:18px">
          &rarr;
        </span>
      </button>

      {/* Fine print */}
      <div style="display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:14px;margin-top:14px;font-family:'Nunito',sans-serif;font-size:12px;color:rgba(255,255,255,0.35);text-align:center">
        <span style="display:flex;align-items:center;gap:5px">&#x1F512; Secure Checkout</span>
        <span style="display:flex;align-items:center;gap:5px">&#x2726; No Charge Today</span>
        <span style="display:flex;align-items:center;gap:5px">&#x2726; No Commitment</span>
      </div>
    </div>
  </>
)

// ─── FREE TOKENS MODAL – Logged-out split layout ──────────────────────────────

const FreeTokensSplitLoggedOut = (props: { onClose: () => void; onAuthSuccess: () => void }) => {
  const settingState = settingStore()
  const google = useGoogleReady()
  const [authMode, setAuthMode] = createSignal<'register' | 'login'>('register')
  const [loading, setLoading] = createSignal(false)

  let googleApi: any = null

  createEffect(() => {
    if (!google()) return
    const win: any = window
    const api = win.google?.accounts?.id
    if (!api || !settingState.config.serverConfig?.googleClientId) return
    api.initialize({
      client_id: settingState.config.serverConfig.googleClientId,
      callback: (result: any) => {
        userStore.handleGoogleCallback('login', result, () => props.onAuthSuccess())
      },
    })
    googleApi = api
  })

  const handleGoogleClick = () => {
    if (!googleApi) return
    googleApi.prompt((notification: any) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        const tempDiv = document.createElement('div')
        tempDiv.style.position = 'fixed'
        tempDiv.style.top = '-9999px'
        document.body.appendChild(tempDiv)
        googleApi.renderButton(tempDiv, { theme: 'filled_black', size: 'large', type: 'standard' })
        setTimeout(() => {
          const btn =
            tempDiv.querySelector('[role="button"]') || tempDiv.querySelector('div[tabindex="0"]')
          if (btn) (btn as HTMLElement).click()
          setTimeout(() => document.body.removeChild(tempDiv), 5000)
        }, 300)
      }
    })
  }

  const handleRegister = async (e: SubmitEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    const data = new FormData(form)
    const handle = (data.get('username') as string) ?? ''
    const email = (data.get('email') as string) ?? ''
    const password = (data.get('password') as string) ?? ''
    setLoading(true)
    await userStore.register({ handle, username: handle, password, email })
    setLoading(false)
  }

  const handleLogin = async (e: SubmitEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    const data = new FormData(form)
    const identifier = (data.get('identifier') as string) ?? ''
    const password = (data.get('password') as string) ?? ''
    setLoading(true)
    await userStore.login(identifier, password)
    setLoading(false)
  }

  const inputStyle =
    "width:100%;height:44px;padding:0 14px;border-radius:10px;border:1.5px solid rgba(255,255,255,0.1);background:rgba(255,255,255,0.04);color:#fff;font-family:'Nunito',sans-serif;font-size:14px;box-sizing:border-box;transition:border-color 0.2s,background 0.2s"
  const labelStyle =
    "display:block;font-family:'Rajdhani',sans-serif;font-size:12px;font-weight:600;letter-spacing:0.08em;text-transform:uppercase;color:rgba(255,255,255,0.55);margin-bottom:6px"

  return (
    <div style="display:flex;flex-direction:column">
      {/* Banner (compact, always shown) */}
      <div
        class="tm-banner"
        style="padding:32px 36px 24px;background:linear-gradient(135deg,#1a1040 0%,#0B0B0F 50%,#13102a 100%);border-bottom:1px solid rgba(138,43,226,0.18);position:relative"
      >
        <CloseBtn onClose={props.onClose} top={16} right={16} size={32} />
        <div class="tm-eyebrow">Limited Time Offer</div>
        <div style="font-family:'Bebas Neue',sans-serif;font-weight:900;line-height:0.98;text-transform:uppercase;position:relative;z-index:1;margin-bottom:10px;font-size:clamp(36px,5vw,52px)">
          <span style="color:#fff">Get </span>
          <span style="color:#00E5FF;text-shadow:0 0 16px rgba(0,229,255,0.55)">25 Tokens</span>
          <br />
          <span style="color:rgba(255,255,255,0.92)">Completely </span>
          <span style="color:#fff">Free</span>
        </div>
        <div style="font-family:'Nunito',sans-serif;font-size:14px;line-height:1.55;color:rgba(255,255,255,0.6);max-width:380px;position:relative;z-index:1">
          Unlock live chats and AI image generation — no charge, ever.
        </div>
      </div>

      {/* Content area: split on desktop, auth-only on mobile */}
      <div style="display:flex;flex:1;min-height:0">
        {/* Left column: offer (desktop only) */}
        <div
          class="tm-split-left"
          style="display:none;padding:24px 28px;border-right:1px solid rgba(255,255,255,0.08);flex:1"
        >
          {/* Perks */}
          <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:20px">
            <For each={FREE_PERKS}>
              {(perk) => (
                <div style="display:flex;align-items:center;gap:6px;font-family:'Rajdhani',sans-serif;font-size:12.5px;font-weight:600;color:rgba(255,255,255,0.65)">
                  <span style="width:6px;height:6px;border-radius:50%;background:#8A2BE2;flex-shrink:0" />
                  {perk}
                </div>
              )}
            </For>
          </div>
          <FreeTokenCard />
        </div>

        {/* Right column: auth */}
        <div style="flex:1;padding:24px 28px;overflow-y:auto">
          {/* Auth heading */}
          <div style="font-family:'Rajdhani',sans-serif;font-size:15px;font-weight:700;text-align:center;color:rgba(255,255,255,0.85);margin-bottom:20px;letter-spacing:0.04em">
            <Show when={authMode() === 'register'} fallback="Log in to claim your tokens">
              Create your free account to claim
            </Show>
          </div>

          {/* Google button */}
          <button
            type="button"
            onClick={handleGoogleClick}
            style="width:100%;height:46px;display:flex;align-items:center;justify-content:center;gap:10px;border-radius:11px;background:#fff;border:none;cursor:pointer;font-family:'Nunito',sans-serif;font-size:14.5px;font-weight:700;color:#111;transition:opacity 0.2s"
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            <Show when={authMode() === 'register'} fallback="Sign In With Google">
              Continue With Google
            </Show>
          </button>

          {/* OR divider */}
          <div style="display:flex;align-items:center;gap:12px;margin:18px 0">
            <span style="flex:1;height:1px;background:rgba(255,255,255,0.1)" />
            <span style="font-family:'Rajdhani',sans-serif;font-size:11px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:rgba(255,255,255,0.35)">
              or
            </span>
            <span style="flex:1;height:1px;background:rgba(255,255,255,0.1)" />
          </div>

          {/* Register form */}
          <Show when={authMode() === 'register'}>
            <form onSubmit={handleRegister}>
              <div style="display:flex;flex-direction:column;gap:12px;margin-bottom:16px">
                <div>
                  <label style={labelStyle}>Username</label>
                  <input
                    name="username"
                    placeholder="Choose a username"
                    autocomplete="username"
                    required
                    class="tm-auth-input"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    autocomplete="email"
                    required
                    class="tm-auth-input"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Password</label>
                  <input
                    name="password"
                    type="password"
                    placeholder="Create a password"
                    autocomplete="new-password"
                    required
                    class="tm-auth-input"
                    style={inputStyle}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading()}
                class="tm-cta-btn"
                style={`padding:16px;background:linear-gradient(135deg,#8A2BE2,#FF3CAC);border-radius:10px;font-family:'Rajdhani',sans-serif;font-size:15px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#fff;box-shadow:0 8px 30px rgba(138,43,226,0.35);opacity:${
                  loading() ? '0.7' : '1'
                }`}
              >
                {loading() ? 'Creating Account…' : 'Create Account & Claim 25 Tokens'}
              </button>
            </form>
          </Show>

          {/* Login form */}
          <Show when={authMode() === 'login'}>
            <form onSubmit={handleLogin}>
              <div style="display:flex;flex-direction:column;gap:12px;margin-bottom:16px">
                <div>
                  <label style={labelStyle}>Email / Username</label>
                  <input
                    name="identifier"
                    placeholder="Enter your email or username"
                    autocomplete="username"
                    required
                    class="tm-auth-input"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Password</label>
                  <input
                    name="password"
                    type="password"
                    placeholder="Your password"
                    autocomplete="current-password"
                    required
                    class="tm-auth-input"
                    style={inputStyle}
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading()}
                class="tm-cta-btn"
                style={`padding:16px;background:linear-gradient(135deg,#8A2BE2,#FF3CAC);border-radius:10px;font-family:'Rajdhani',sans-serif;font-size:15px;font-weight:800;letter-spacing:0.08em;text-transform:uppercase;color:#fff;box-shadow:0 8px 30px rgba(138,43,226,0.35);opacity:${
                  loading() ? '0.7' : '1'
                }`}
              >
                {loading() ? 'Logging In…' : 'Log In & Claim'}
              </button>
            </form>
          </Show>

          {/* Toggle link */}
          <div style="margin-top:16px;text-align:center;font-family:'Rajdhani',sans-serif;font-size:13px;font-weight:600;color:rgba(255,255,255,0.5)">
            <Show
              when={authMode() === 'register'}
              fallback={
                <>
                  Don't have an account?{' '}
                  <a
                    href="#"
                    style="color:#FF3CAC;font-weight:700;text-decoration:none"
                    onClick={(e) => {
                      e.preventDefault()
                      setAuthMode('register')
                    }}
                  >
                    Sign Up
                  </a>
                </>
              }
            >
              Already have an account?{' '}
              <a
                href="#"
                style="color:#FF3CAC;font-weight:700;text-decoration:none"
                onClick={(e) => {
                  e.preventDefault()
                  setAuthMode('login')
                }}
              >
                Log In
              </a>
            </Show>
          </div>

          {/* Legal */}
          <div style="margin-top:14px;text-align:center;font-family:'Nunito',sans-serif;font-size:11px;line-height:1.5;color:rgba(255,255,255,0.28)">
            By continuing you confirm you are 18+ and agree to our Terms &amp; Privacy.
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Root modal component ─────────────────────────────────────────────────────

const BuyTokensModal = () => {
  const state = userStore()

  const [showPayment, setShowPayment] = createSignal(false)
  const [paymentUrl, setPaymentUrl] = createSignal('')

  const hasClaimedInitial = createMemo(() => state.profile?.hasClaimedInitialTokens ?? false)
  const isLoggedIn = createMemo(() => state.loggedIn)
  // #212: the out-of-tokens ("Running On Empty") popup reuses this modal's paid flow
  // with an 'empty' banner. It's driven by the shared showNoTokensModal flag (fired
  // from image-gen, chat, and message-send when a balance check fails).
  const outOfTokens = createMemo(() => state.showNoTokensModal)
  // Never show the free-claim modal in the out-of-tokens context — always the paid flow.
  const showFreeModal = createMemo(() => !hasClaimedInitial() && !outOfTokens())
  const showSplit = createMemo(() => showFreeModal() && !isLoggedIn())

  const isHentaiPath = () =>
    window.location.pathname.startsWith('/hentai') ||
    window.location.pathname.startsWith('/lp/hentai')

  const closeModal = () => {
    userStore.buyTokensModal(false)
    userStore.showNoTokensModal(false) // #212: also clear the out-of-tokens trigger
    setShowPayment(false)
    setPaymentUrl('')
  }

  // ESC key + body scroll lock
  createEffect(() => {
    const shown = state.showBuyTokens || state.showNoTokensModal
    if (!shown) return

    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal()
    }
    window.addEventListener('keydown', onKey)

    onCleanup(() => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', onKey)
    })
  })

  // Payment success via iframe postMessage
  onMount(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'payment-success') {
        setShowPayment(false)
        setPaymentUrl('')
        toastStore.success('Payment successful! Your tokens have been added.')
        userStore.buyTokensModal(false)
      }
    }
    window.addEventListener('message', handleMessage)
    onCleanup(() => window.removeEventListener('message', handleMessage))
  })

  const handlePurchase = async (id: number, price: string, amount: number) => {
    try {
      const response = await aifansSubscriptionApi.createTokenReloadLink(price, id, amount)
      if (response.result?.redirectURL) {
        setPaymentUrl(response.result.redirectURL)
        setShowPayment(true)
      }
    } catch {
      toastStore.error('There was a problem processing your request. Please try again.')
    }
  }

  const handleClaim = async () => {
    try {
      const response = await aifansSubscriptionApi.createTokenReloadLink('0.00', 0, 25)
      if (response.result?.redirectURL) {
        setPaymentUrl(response.result.redirectURL)
        setShowPayment(true)
      }
    } catch {
      toastStore.error('There was a problem processing your request. Please try again.')
    }
  }

  // ── Overlay & container tokens vary between buy / free modals ──────────────
  const overlayBg = createMemo(() => (showFreeModal() ? 'rgba(0,0,0,0.88)' : 'rgba(0,0,0,0.84)'))
  const containerMaxWidth = createMemo(() => {
    if (!showFreeModal()) return '520px'
    return showSplit() ? '880px' : '620px'
  })
  const containerBg = createMemo(() =>
    showFreeModal() ? '#13102a' : 'linear-gradient(180deg,#14101f 0%,#0c0916 100%)'
  )
  const containerBorder = createMemo(() =>
    showFreeModal() ? '1.5px solid rgba(140,80,240,0.5)' : '1.5px solid rgba(138,43,226,0.5)'
  )
  const containerShadow = createMemo(() =>
    showFreeModal()
      ? '0 0 60px rgba(140,80,240,0.15), 0 30px 80px rgba(0,0,0,0.6)'
      : '0 0 60px rgba(138,43,226,0.15), 0 30px 80px rgba(0,0,0,0.6)'
  )
  const overlayZIndex = createMemo(() => (showFreeModal() ? 1000 : 200))

  return (
    <Show when={(state.showBuyTokens || outOfTokens()) && !isHentaiPath()}>
      {/* Overlay */}
      <div
        class={showFreeModal() ? 'tm-overlay' : 'bt-overlay'}
        style={`position:fixed;top:0;left:0;right:0;height:100dvh;box-sizing:border-box;z-index:${overlayZIndex()};background:${overlayBg()};backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;padding:calc(20px + env(safe-area-inset-top)) calc(20px + env(safe-area-inset-right)) calc(20px + env(safe-area-inset-bottom)) calc(20px + env(safe-area-inset-left))`}
        onClick={closeModal}
      >
        {/* Container */}
        <div
          class={showFreeModal() ? 'tm-container' : 'bt-container'}
          style={`position:relative;width:100%;max-width:${containerMaxWidth()};border-radius:16px;background:${containerBg()};border:${containerBorder()};box-shadow:${containerShadow()};max-height:calc(100dvh - 40px - env(safe-area-inset-top) - env(safe-area-inset-bottom));overflow-y:auto`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Payment iframe */}
          <Show
            when={!showPayment()}
            fallback={
              <div style="position:relative">
                <CloseBtn onClose={closeModal} />
                <div style="height:740px;max-height:calc(100vh - 100px);width:100%">
                  <iframe
                    src={paymentUrl()}
                    style="width:100%;height:100%;border:0"
                    allow="payment"
                  />
                </div>
              </div>
            }
          >
            {/* Buy tokens (paid) */}
            <Show
              when={!showFreeModal()}
              fallback={
                /* Free token modal */
                <Show
                  when={showSplit()}
                  fallback={<FreeTokensSingleColumn onClose={closeModal} onClaim={handleClaim} />}
                >
                  <FreeTokensSplitLoggedOut onClose={closeModal} onAuthSuccess={closeModal} />
                </Show>
              }
            >
              <BuyTokensFlow
                onClose={closeModal}
                onPurchase={handlePurchase}
                variant={outOfTokens() ? 'empty' : 'default'}
              />
            </Show>
          </Show>
        </div>
      </div>
    </Show>
  )
}

export default BuyTokensModal
